import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  addRiskReview,
  attestRisk,
  closeRisk,
  createRisk,
  getMe,
  getRiskMethodology,
  linkRiskControl,
  listComplianceControls,
  listRiskReviews,
  listRisks,
  listUsers,
  unlinkRiskControl,
  updateRisk,
  type RiskStatus,
  type RiskView,
  type TreatmentOption,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const CATEGORIES = [
  "access",
  "data",
  "availability",
  "supplier",
  "people",
  "legal",
  "development",
  "physical",
] as const;

const STATUS_STYLE: Record<RiskStatus, { label: string; cls: string }> = {
  identified: { label: "Identified", cls: "border border-border text-text-secondary" },
  assessed: { label: "Assessed", cls: "bg-accent-muted text-accent" },
  treated: { label: "Treated", cls: "bg-accent-muted text-accent" },
  accepted: { label: "Accepted", cls: "bg-success/15 text-success" },
  closed: { label: "Closed", cls: "border border-border text-text-muted" },
};

/** Score bands read as a ramp: low → high, using the categorical mark hues. */
function scoreCls(score: number | null, threshold: number): string {
  if (score == null) return "text-text-muted";
  if (score > threshold * 2) return "text-danger font-semibold";
  if (score > threshold) return "text-warning font-semibold";
  return "text-success";
}

export function ComplianceRisksPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const { data: risks, isLoading } = useQuery({ queryKey: ["compliance-risks"], queryFn: listRisks });
  const { data: methodology } = useQuery({
    queryKey: ["risk-methodology"],
    queryFn: getRiskMethodology,
  });

  const openId = params.get("open");
  const open = useMemo(() => risks?.find((r) => r.risk.id === openId) ?? null, [risks, openId]);
  const shown = (risks ?? []).filter((r) => showClosed || r.status !== "closed");
  const threshold = methodology?.appetiteThreshold ?? 8;

  return (
    <div>
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Risk register</h1>
          <p className="mt-2 text-text-secondary">
            Scored on the published methodology, treated by linked controls, and accepted
            by the named risk owner — which ISO asks for specifically.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />
            Show closed
          </label>
          <button
            onClick={() => setAddOpen(true)}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          >
            New risk
          </button>
        </div>
      </header>

      {methodology && (
        <p className="mb-4 rounded-token-lg border border-border bg-bg-card px-4 py-2.5 text-xs text-text-muted">
          Appetite: residual ≤ <span className="text-text-heading">{threshold}</span> is within
          appetite (score = likelihood × impact, 1–25). Reassess every{" "}
          {methodology.reviewCadenceMonths} months.
        </p>
      )}

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : shown.length === 0 ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-heading">
              No risks recorded
            </h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              The register is what SoA justifications hang off — start with the obvious ones:
              leaver access, vendor outage, laptop loss, key-person dependency.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Risk</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Inherent</th>
                <th className="px-4 py-2.5 font-semibold">Treatment</th>
                <th className="px-4 py-2.5 font-semibold">Residual</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => (
                <tr
                  key={v.risk.id}
                  className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                  onClick={() => setParams({ open: v.risk.id })}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">
                    {v.risk.ref}
                  </td>
                  <td className="px-4 py-2.5 text-text-heading">{v.risk.title}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {v.risk.owner?.name ?? <span className="text-warning">unassigned</span>}
                  </td>
                  <td className={`px-4 py-2.5 ${scoreCls(v.inherentScore, threshold)}`}>
                    {v.inherentScore ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 capitalize text-text-secondary">
                    {v.risk.treatmentOption ?? "—"}
                  </td>
                  <td className={`px-4 py-2.5 ${scoreCls(v.residualScore, threshold)}`}>
                    {v.residualScore ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[v.status].cls}`}
                    >
                      {STATUS_STYLE[v.status].label}
                    </span>
                    {v.blockers.length > 0 && (
                      <span className="ml-2 text-xs text-text-muted">{v.blockers.length}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <RiskModal view={open} threshold={threshold} onClose={() => setParams({})} />}
      {addOpen && (
        <NewRiskModal
          onClose={() => setAddOpen(false)}
          onCreated={(id) => {
            void qc.invalidateQueries({ queryKey: ["compliance-risks"] });
            setParams({ open: id });
          }}
        />
      )}
    </div>
  );
}

function NewRiskModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>("access");

  const create = useMutation({
    mutationFn: () => createRisk({ title: title.trim(), description: description.trim(), category }),
    onSuccess: (v) => {
      onCreated(v.risk.id);
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="New risk"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!title.trim() || !description.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Create
        </button>
      }
    >
      <div className="space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Former contractor retains GitHub access"
          className={inputCls}
          autoFocus
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="The scenario: what could happen, to what, and how."
          className={inputCls}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

function RiskModal({
  view,
  threshold,
  onClose,
}: {
  view: RiskView;
  threshold: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const r = view.risk;
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const { data: methodology } = useQuery({
    queryKey: ["risk-methodology"],
    queryFn: getRiskMethodology,
  });
  const { data: controls } = useQuery({
    queryKey: ["compliance-controls"],
    queryFn: listComplianceControls,
  });
  const { data: reviews } = useQuery({
    queryKey: ["risk-reviews", r.id],
    queryFn: () => listRiskReviews(r.id),
  });

  const [form, setForm] = useState({
    inherentLikelihood: r.inherentLikelihood?.toString() ?? "",
    inherentImpact: r.inherentImpact?.toString() ?? "",
    treatmentOption: r.treatmentOption ?? "",
    treatmentPlan: r.treatmentPlan ?? "",
    residualLikelihood: r.residualLikelihood?.toString() ?? "",
    residualImpact: r.residualImpact?.toString() ?? "",
  });
  const [statement, setStatement] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [linkId, setLinkId] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [r.id]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-risks"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
    void qc.invalidateQueries({ queryKey: ["risk-reviews", r.id] });
    void qc.invalidateQueries({ queryKey: ["compliance-soa"] });
  };
  const fail = (e: Error) => setError(e.message);

  const save = useMutation({
    mutationFn: () =>
      updateRisk(r.id, {
        inherentLikelihood: form.inherentLikelihood === "" ? null : Number(form.inherentLikelihood),
        inherentImpact: form.inherentImpact === "" ? null : Number(form.inherentImpact),
        treatmentOption: (form.treatmentOption || null) as TreatmentOption | null,
        treatmentPlan: form.treatmentPlan || null,
        residualLikelihood: form.residualLikelihood === "" ? null : Number(form.residualLikelihood),
        residualImpact: form.residualImpact === "" ? null : Number(form.residualImpact),
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const setOwner = useMutation({
    mutationFn: (ownerId: string | null) => updateRisk(r.id, { ownerId }),
    onSuccess: invalidate,
    onError: fail,
  });

  const attest = useMutation({
    mutationFn: (action: "prepared" | "reviewed" | "approved") => attestRisk(r.id, action, statement),
    onSuccess: () => {
      setStatement("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const link = useMutation({
    mutationFn: () => linkRiskControl(r.id, linkId),
    onSuccess: () => {
      setLinkId("");
      invalidate();
    },
    onError: fail,
  });

  const unlink = useMutation({
    mutationFn: (controlId: string) => unlinkRiskControl(r.id, controlId),
    onSuccess: invalidate,
    onError: fail,
  });

  const review = useMutation({
    mutationFn: () => addRiskReview(r.id, reviewNote),
    onSuccess: () => {
      setReviewNote("");
      invalidate();
    },
    onError: fail,
  });

  const close = useMutation({
    mutationFn: (reason: string) => closeRisk(r.id, reason),
    onSuccess: invalidate,
    onError: fail,
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  const nextAction =
    view.cycleStage === "none"
      ? { action: "prepared" as const, label: "Submit assessment" }
      : view.cycleStage === "prepared"
        ? { action: "reviewed" as const, label: "Review assessment" }
        : view.cycleStage === "reviewed"
          ? { action: "approved" as const, label: "Accept residual risk" }
          : null;
  const isOwner = me?.id === r.ownerId;
  const linkable = (controls ?? []).filter((c) => !view.controls.some((x) => x.id === c.control.id));

  return (
    <Modal open onClose={onClose} title={`${r.ref} — ${r.title}`} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[view.status].cls}`}
          >
            {STATUS_STYLE[view.status].label}
          </span>
          <span className="text-text-muted">
            {r.category} · inherent {view.inherentScore ?? "—"} → residual{" "}
            <span className={scoreCls(view.residualScore, threshold)}>{view.residualScore ?? "—"}</span>
          </span>
          <label className="ml-auto flex items-center gap-2 text-text-secondary">
            Risk owner
            <select
              value={r.ownerId ?? ""}
              onChange={(e) => setOwner.mutate(e.target.value || null)}
              className="rounded-md border border-border bg-bg-card px-2 py-1 text-sm text-text-heading"
            >
              <option value="">— unassigned —</option>
              {users
                ?.filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <p className="whitespace-pre-wrap text-text-secondary">{r.description}</p>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b) => (
              <li key={b.rule} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        <Section title="Assessment">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <ScoreSelect
              label="Inherent likelihood"
              value={form.inherentLikelihood}
              scale={methodology?.likelihoodScale}
              onChange={(v) => setForm({ ...form, inherentLikelihood: v })}
            />
            <ScoreSelect
              label="Inherent impact"
              value={form.inherentImpact}
              scale={methodology?.impactScale}
              onChange={(v) => setForm({ ...form, inherentImpact: v })}
            />
            <ScoreSelect
              label="Residual likelihood"
              value={form.residualLikelihood}
              scale={methodology?.likelihoodScale}
              onChange={(v) => setForm({ ...form, residualLikelihood: v })}
            />
            <ScoreSelect
              label="Residual impact"
              value={form.residualImpact}
              scale={methodology?.impactScale}
              onChange={(v) => setForm({ ...form, residualImpact: v })}
            />
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-[12rem_1fr]">
            <label className="text-xs text-text-muted">
              Treatment
              <select
                value={form.treatmentOption}
                onChange={(e) => setForm({ ...form, treatmentOption: e.target.value })}
                className={inputCls}
              >
                <option value="">— undecided —</option>
                <option value="modify">modify (apply controls)</option>
                <option value="retain">retain (accept as-is)</option>
                <option value="avoid">avoid (stop the activity)</option>
                <option value="share">share (insure / transfer)</option>
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Treatment plan
              <textarea
                value={form.treatmentPlan}
                onChange={(e) => setForm({ ...form, treatmentPlan: e.target.value })}
                rows={2}
                placeholder="What we are doing about it, by when, by whom."
                className={inputCls}
              />
            </label>
          </div>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="mt-2 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Save assessment
          </button>
          {view.cycleStage === "approved" && (
            <span className="ml-3 text-xs text-warning">
              Re-scoring an accepted risk voids the acceptance — the owner must accept again.
            </span>
          )}
        </Section>

        <Section title={`Treated by — ${view.controls.length} control(s)`}>
          <ul className="space-y-1">
            {view.controls.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-text-secondary">
                <span className="font-medium text-text-heading">{c.ref}</span> {c.name}
                <button
                  onClick={() => unlink.mutate(c.id)}
                  className="ml-auto text-xs text-text-muted hover:text-danger"
                >
                  <i className="fa-solid fa-link-slash" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2">
            <select
              value={linkId}
              onChange={(e) => setLinkId(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-2 py-1.5 text-xs text-text-heading"
            >
              <option value="">— link a control —</option>
              {linkable.map((c) => (
                <option key={c.control.id} value={c.control.id}>
                  {c.control.ref} · {c.control.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => link.mutate()}
              disabled={!linkId || link.isPending}
              className="rounded-token-md border border-border bg-bg-card px-3 py-1.5 text-xs font-medium text-text-heading hover:bg-bg-card-hover disabled:opacity-60"
            >
              Link
            </button>
          </div>
        </Section>

        {!r.closedAt && (
          <Section title={`Sign-off — ${view.cycleStage}`}>
            {nextAction && (
              <div className="flex gap-2">
                <input
                  value={statement}
                  onChange={(e) => setStatement(e.target.value)}
                  placeholder={
                    nextAction.action === "approved"
                      ? "I accept this residual risk on behalf of the business."
                      : "What are you asserting?"
                  }
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
                />
                <button
                  onClick={() => attest.mutate(nextAction.action)}
                  disabled={!statement.trim() || attest.isPending}
                  className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {nextAction.label}
                </button>
              </div>
            )}
            {nextAction?.action === "approved" && !isOwner && (
              <p className="mt-2 text-xs text-text-muted">
                Only {r.owner?.name ?? "the risk owner"} can accept this — ISO 27001 6.1.3(f)
                asks for the risk owner's acceptance specifically.
              </p>
            )}
            {!nextAction && (
              <p className="text-text-secondary">
                Accepted by {r.owner?.name ?? "the risk owner"}
                {r.lastReviewedAt && ` on ${new Date(r.lastReviewedAt).toLocaleDateString()}`}.
              </p>
            )}
          </Section>
        )}

        <Section title="Reassessments">
          <ul className="space-y-1">
            {reviews?.map((rv) => (
              <li key={rv.id} className="text-text-secondary">
                <span className="font-medium text-text-heading">{rv.reviewedBy?.name ?? "?"}</span> ·{" "}
                {new Date(rv.reviewedAt).toLocaleDateString()} · residual {rv.residualScore ?? "—"}
                <div className="ml-4 text-xs italic text-text-muted">“{rv.note}”</div>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2">
            <input
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder="Reassessment note — what changed, or that nothing did."
              className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
            />
            <button
              onClick={() => review.mutate()}
              disabled={!reviewNote.trim() || review.isPending}
              className="rounded-token-md border border-border bg-bg-card px-3 py-2 text-sm font-medium text-text-heading hover:bg-bg-card-hover disabled:opacity-60"
            >
              Record review
            </button>
          </div>
        </Section>

        {!r.closedAt && (
          <button
            onClick={() => {
              const reason = window.prompt("Why is this risk being closed?");
              if (reason?.trim()) close.mutate(reason.trim());
            }}
            className="text-xs text-text-muted hover:text-danger"
          >
            Close this risk
          </button>
        )}
        {r.closedAt && (
          <p className="text-xs text-text-muted">
            Closed {new Date(r.closedAt).toLocaleDateString()} — {r.closureReason}
          </p>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function ScoreSelect({
  label,
  value,
  scale,
  onChange,
}: {
  label: string;
  value: string;
  scale?: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="text-xs text-text-muted">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
      >
        <option value="">—</option>
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={n} title={scale?.[n - 1]}>
            {n} · {scale?.[n - 1]?.split(" — ")[0] ?? n}
          </option>
        ))}
      </select>
    </label>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
